import { createPortal } from 'react-dom'
import { useState, useEffect, useRef } from 'react'
import { usePanelStore } from '../../../lib/panelStore'
import { ChevronDoubleRightIcon } from '../tasks/components/TaskIcons'
import { useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { apiFetch } from '@/lib/api'

interface CreateWorkspacePanelProps {
  isOpen: boolean
  onClose: () => void
  onWorkspaceCreated?: () => void
}

export function CreateWorkspacePanel({
  isOpen,
  onClose,
  onWorkspaceCreated,
}: CreateWorkspacePanelProps) {
  const navigate = useNavigate()
  const setIsPanelOpen = usePanelStore((state) => state.setIsPanelOpen)
  const panelRef = useRef<HTMLDivElement>(null)

  // Form Data
  const [workspaceName, setWorkspaceName] = useState('')
  const [teamSize, setTeamSize] = useState('1-10')
  const [industry, setIndustry] = useState('Technology')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Sync global panel state
  useEffect(() => {
    setIsPanelOpen(isOpen)
  }, [isOpen, setIsPanelOpen])

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  const generateSlug = (name: string) => {
    return (
      name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'workspace'
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const slug = generateSlug(workspaceName) + '-' + Math.random().toString(36).substring(2, 6)

      const wsResponse = await apiFetch('/api/workspaces', {
        method: 'POST',
        body: JSON.stringify({
          name: workspaceName,
          slug: slug,
          teamSize: teamSize,
          industry: industry,
        }),
      })

      if (!wsResponse.ok) {
        const errorText = await wsResponse.text()
        throw new Error(`Failed to create workspace: ${errorText}`)
      }

      // Success
      setWorkspaceName('')
      setTeamSize('1-10')
      setIndustry('Technology')
      onClose()

      if (onWorkspaceCreated) {
        onWorkspaceCreated()
      }

      // Navigate to new workspace
      navigate({ to: `/${slug}` })
    } catch (err: any) {
      console.error(err)
      setError(err.message || 'Error creating workspace')
    } finally {
      setLoading(false)
    }
  }

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={`fixed inset-0 z-50 flex w-full max-w-none transform flex-col rounded-none bg-[#12121a] shadow-2xl transition-transform duration-300 ease-out sm:inset-auto sm:bottom-4 sm:right-4 sm:top-4 sm:w-[448px] sm:max-w-md sm:rounded-2xl ${isOpen ? 'translate-x-0' : 'translate-x-full sm:translate-x-[calc(100%+2rem)]'}`}
      >
        {/* Header */}
        <div className="flex-none border-b border-gray-800 p-6">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-800 hover:text-white"
            >
              <ChevronDoubleRightIcon />
            </button>
            <div>
              <h2 className="text-lg font-semibold text-white">New Workspace</h2>
              <p className="text-xs text-gray-500">Create a new space for your team</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 rounded-lg bg-red-500/10 p-3 text-sm text-red-500">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="workspaceName" className="text-sm text-gray-400">
                Workspace Name
              </Label>
              <Input
                id="workspaceName"
                type="text"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                className="rounded-none border-gray-800 bg-[#1a1a24] py-5 text-white placeholder-gray-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500/20 sm:rounded-xl"
                placeholder="My Company"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="teamSize" className="text-sm text-gray-400">
                Team Size
              </Label>
              <div className="relative">
                <select
                  id="teamSize"
                  value={teamSize}
                  onChange={(e) => setTeamSize(e.target.value)}
                  className="w-full cursor-pointer appearance-none rounded-none border border-gray-800 bg-[#1a1a24] px-4 py-3 text-white transition-colors focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/20 sm:rounded-xl"
                  required
                >
                  <option value="1-10">1-10 people</option>
                  <option value="11-50">11-50 people</option>
                  <option value="51-200">51-200 people</option>
                  <option value="200+">200+ people</option>
                </select>
                <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                  <svg
                    width="10"
                    height="6"
                    viewBox="0 0 10 6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M1 1L5 5L9 1" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="industry" className="text-sm text-gray-400">
                Industry
              </Label>
              <div className="relative">
                <select
                  id="industry"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="w-full cursor-pointer appearance-none rounded-none border border-gray-800 bg-[#1a1a24] px-4 py-3 text-white transition-colors focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/20 sm:rounded-xl"
                  required
                >
                  <option value="Technology">Technology / IT</option>
                  <option value="Gamedev">Gamedev / Games</option>
                  <option value="Marketing">Marketing / Agency</option>
                  <option value="Finance">Finance</option>
                  <option value="Education">Education</option>
                  <option value="Health">Health</option>
                  <option value="E-commerce">E-commerce</option>
                  <option value="Other">Other</option>
                </select>
                <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                  <svg
                    width="10"
                    height="6"
                    viewBox="0 0 10 6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M1 1L5 5L9 1" />
                  </svg>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex-none rounded-b-2xl border-t border-gray-800 bg-[#0f0f14] p-6">
          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              onClick={onClose}
              variant="ghost"
              className="text-gray-400 hover:bg-gray-800 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="min-w-[100px] bg-amber-500 font-medium text-black hover:bg-amber-400"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <svg
                    className="h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating
                </div>
              ) : (
                'Create Workspace'
              )}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body
  )
}
